import React, { Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { RouterProvider, createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";

import { installMarketingCtaTracking, trackPageView } from "./lib/analytics";
import { getPageMetadataSnapshot, isPageMetadataReadyForNavigation } from "./lib/pageViewMetadata";

import About from "./components/About";
import PageNotFound from "./components/PageNotFound";
import MainPage from "./components/MainPage";
import ContactUs from "./components/ContactUs";
import EarlyAdopter from "./components/EarlyAdopter";
import FAQ from "./components/FAQ";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";
import Terms from "./components/Terms";
import Privacy from "./components/Privacy";
import DPA from "./components/DPA";
import Subprocessors from "./components/Subprocessors";
import FeaturesPage from "./pages/FeaturesPage"; // Features page (public)
import PricingPage from "./pages/PricingPage";
import MenuTrainingPage from "./pages/MenuTrainingPage";
import MenuTrainingTemplatePage from "./pages/MenuTrainingTemplatePage";
import MenuTrainingChecklistPage from "./pages/MenuTrainingChecklistPage";
import ServerTrainingPage from "./pages/ServerTrainingPage";
import ServerTrainingChecklistPage from "./pages/ServerTrainingChecklistPage";
import WineTrainingPage from "./pages/WineTrainingPage";
import WineTrainingPairingGuidePage from "./pages/WineTrainingPairingGuidePage";
import AllergenSafetyPage from "./pages/AllergenSafetyPage";
import AllergenSafetyQAGuidePage from "./pages/AllergenSafetyQAGuidePage";
import AssessmentPage from "./pages/AssessmentPage";
import Disclaimer from "./components/Disclaimer";
import RestaurantOnboardingPage from "./pages/RestaurantOnboardingPage";
import AnalyticsConsent from "./components/AnalyticsConsent";

const Admin = lazy(() => import("./components/Admin"));
const AdminGate = lazy(() => import("./components/AdminGate"));
const AdminAssessmentsTab = lazy(() => import("./components/AdminAssessmentsTab"));
const AdminUsersTab = lazy(() => import("./components/AdminUsersTab"));
const AdminCampaignLeadsTab = lazy(() => import("./components/AdminCampaignLeadsTab"));
const AdminPlaybookTab = lazy(() => import("./components/AdminPlaybookTab"));
const AdminPartnerIntakesTab = lazy(() => import("./components/AdminPartnerIntakesTab"));
const BlogCMS = lazy(() => import("./components/BlogCMS"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AssessmentReport = lazy(() => import("./pages/AssessmentReport"));
const AssessmentReportPrint = lazy(() => import("./pages/AssessmentReportPrint"));
const DevAutofillAssessment = lazy(() => import("./pages/DevAutofillAssessment"));
const NycEarlyAccessPage = lazy(() => import("./pages/NycEarlyAccessPage"));
const NycEarlyAccessThankYouPage = lazy(() => import("./pages/NycEarlyAccessThankYouPage"));
const MassRestaurantsUnitedPage = lazy(() => import("./pages/MassRestaurantsUnitedPage"));
const NorthFlatbushBidPage = lazy(() => import("./pages/NorthFlatbushBidPage"));
const EatDenverPage = lazy(() => import("./pages/EatDenverPage"));
const DowntownBrooklynPartnershipPage = lazy(() => import("./pages/DowntownBrooklynPartnershipPage"));
const FultonBusinessAlliancePage = lazy(() => import("./pages/FultonBusinessAlliancePage"));

function RouteLoadingFallback({ label = "Loading page" }) {
  return (
    <main className="container py-5" role="status" aria-live="polite" aria-busy="true">
      <p className="mb-0">{label}...</p>
    </main>
  );
}

function withRouteSuspense(element, label) {
  return <Suspense fallback={<RouteLoadingFallback label={label} />}>{element}</Suspense>;
}

function GaPageViewTracker() {
  const location = useLocation();
  const previousPageLocationRef = useRef("");
  const lastTrackedPathRef = useRef("");
  const lastTrackedMetadataRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const path = `${location.pathname}${location.search || ""}`;
    if (!path) return undefined;

    if (lastTrackedPathRef.current === path) {
      return undefined;
    }

    const pageReferrer = previousPageLocationRef.current || document.referrer || "";
    const startSnapshot = getPageMetadataSnapshot(document, window);
    const referenceSnapshot = lastTrackedMetadataRef.current || startSnapshot;
    const isInitialNavigation = !previousPageLocationRef.current;
    let hasObservedMetadataMutation = false;
    let observer = null;
    let active = true;

    const cleanup = () => {
      window.removeEventListener("sym:analytics-ready", onAnalyticsReady);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    const attemptTrack = () => {
      if (!active) return true;
      if (lastTrackedPathRef.current === path) return true;

      const snapshot = getPageMetadataSnapshot(document, window);
      const ready = isPageMetadataReadyForNavigation({
        pathname: location.pathname || "/",
        startSnapshot,
        referenceSnapshot,
        hasObservedMetadataMutation,
        isInitialNavigation,
        snapshot,
      });
      if (!ready) return false;

      const tracked = trackPageView(path, {
        title: snapshot.title || document.title,
        pageLocation: window.location.href,
        pageReferrer,
      });

      if (tracked) {
        lastTrackedPathRef.current = path;
        previousPageLocationRef.current = window.location.href;
        lastTrackedMetadataRef.current = snapshot;
      }

      return tracked;
    };

    const onAnalyticsReady = () => {
      if (attemptTrack()) {
        if (observer) observer.disconnect();
      }
    };
    window.addEventListener("sym:analytics-ready", onAnalyticsReady);
    if (!attemptTrack()) {
      observer = new MutationObserver(() => {
        hasObservedMetadataMutation = true;
        onAnalyticsReady();
      });
      observer.observe(document.head || document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["href"],
        characterData: true,
      });
    }

    return () => {
      active = false;
      cleanup();
    };
  }, [location.pathname, location.search]);

  return null;
}

function MarketingCtaTracker() {
  useEffect(() => installMarketingCtaTracking(), []);
  return null;
}

function RouteScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousScrollRestoration = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    if (location.hash) {
      const targetId = decodeURIComponent(location.hash.slice(1));
      const scrollToHashTarget = () => {
        const target = document.getElementById(targetId);
        if (target) {
          target.scrollIntoView({ block: "start", behavior: "auto" });
        }
      };

      const frameId = window.requestAnimationFrame(scrollToHashTarget);

      return () => {
        window.cancelAnimationFrame(frameId);
        if ("scrollRestoration" in window.history) {
          window.history.scrollRestoration = previousScrollRestoration;
        }
      };
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    return () => {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = previousScrollRestoration;
      }
    };
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function RootLayout() {
  return (
    <>
      <GaPageViewTracker />
      <MarketingCtaTracker />
      <RouteScrollManager />
      <Outlet />
      <AnalyticsConsent />
    </>
  );
}

function App() {
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: <RootLayout />,
          children: [
            { index: true, element: <MainPage /> },
            { path: "about", element: <About /> },
            // Public Features route
            { path: "features", element: <FeaturesPage /> },
            { path: "pricing", element: <PricingPage /> },
            { path: "menu-training", element: <MenuTrainingPage /> },
            { path: "menu-training/template", element: <MenuTrainingTemplatePage /> },
            { path: "menu-training/checklist", element: <MenuTrainingChecklistPage /> },
            { path: "server-training", element: <ServerTrainingPage /> },
            { path: "server-training/onboarding-checklist", element: <ServerTrainingChecklistPage /> },
            { path: "wine-training", element: <WineTrainingPage /> },
            { path: "wine-training/pairing-guide", element: <WineTrainingPairingGuidePage /> },
            { path: "allergen-safety", element: <AllergenSafetyPage /> },
            { path: "allergen-safety/q-and-a-guide", element: <AllergenSafetyQAGuidePage /> },
            { path: "admin/login", element: withRouteSuspense(<AdminLogin />, "Loading admin sign in") },
            {
              path: "Char3tt34su",
              element: withRouteSuspense(
                <AdminGate>
                  <Admin />
                </AdminGate>,
                "Loading admin dashboard"
              ),
              children: [
                // Redirect the bare admin path to the canonical assessments tab
                { index: true, element: <Navigate to="assessments" replace /> },
                { path: "assessments", element: withRouteSuspense(<AdminAssessmentsTab />, "Loading assessments") },
                { path: "users", element: withRouteSuspense(<AdminUsersTab />, "Loading users") },
                { path: "campaign-leads", element: withRouteSuspense(<AdminCampaignLeadsTab />, "Loading campaign leads") },
                { path: "playbook", element: withRouteSuspense(<AdminPlaybookTab />, "Loading playbook") },
                { path: "mass-restaurants-united", element: withRouteSuspense(<AdminPartnerIntakesTab />, "Loading partner intakes") },
                { path: "north-flatbush-bid", element: withRouteSuspense(<AdminPartnerIntakesTab partnerId="north-flatbush-bid" />, "Loading partner intakes") },
                { path: "eat-denver", element: withRouteSuspense(<AdminPartnerIntakesTab partnerId="eat-denver" />, "Loading partner intakes") },
                { path: "restaurant-onboarding", element: withRouteSuspense(<AdminPartnerIntakesTab partnerId="restaurant-onboarding" />, "Loading partner intakes") },
                { path: "downtown-brooklyn-partnership", element: withRouteSuspense(<AdminPartnerIntakesTab partnerId="downtown-brooklyn-partnership" />, "Loading partner intakes") },
                { path: "fulton-business-alliance", element: withRouteSuspense(<AdminPartnerIntakesTab partnerId="fulton-business-alliance" />, "Loading partner intakes") },
                { path: "blog", element: withRouteSuspense(<BlogCMS />, "Loading blog manager") },
              ],
            },
            // Legacy direct blog route preserved
            {
              path: "Char3tt34su/blog",
              element: withRouteSuspense(
                <AdminGate>
                  <BlogCMS />
                </AdminGate>,
                "Loading blog manager"
              ),
            },
            { path: "blog", element: <BlogList /> },
            { path: "blog/:slug", element: <BlogPost /> },
            { path: "contact", element: <ContactUs /> },
            { path: "earlyadopter", element: <EarlyAdopter /> },
            { path: "nyc-early-access", element: withRouteSuspense(<NycEarlyAccessPage />, "Loading NYC early access") },
            { path: "nyc-early-access/thank-you", element: withRouteSuspense(<NycEarlyAccessThankYouPage />, "Loading thank-you page") },
            { path: "mass-restaurants-united", element: withRouteSuspense(<MassRestaurantsUnitedPage />, "Loading partner intake") },
            { path: "north-flatbush-bid", element: withRouteSuspense(<NorthFlatbushBidPage />, "Loading partner intake") },
            { path: "eat-denver", element: withRouteSuspense(<EatDenverPage />, "Loading partner intake") },
            { path: "restaurant-onboarding", element: <RestaurantOnboardingPage /> },
            { path: "downtown-brooklyn-partnership", element: withRouteSuspense(<DowntownBrooklynPartnershipPage />, "Loading partner intake") },
            { path: "fulton-business-alliance", element: withRouteSuspense(<FultonBusinessAlliancePage />, "Loading partner intake") },
            { path: "faq", element: <FAQ /> },
            { path: "assessment", element: <AssessmentPage /> },
            { path: "assessment/report", element: withRouteSuspense(<AssessmentReport />, "Loading assessment report") },
            { path: "assessment/report/print", element: withRouteSuspense(<AssessmentReportPrint />, "Loading printable report") },
            // Dev helper to prefill answers and jump to report
            { path: "assessment/dev/autofill", element: withRouteSuspense(<DevAutofillAssessment />, "Loading assessment helper") },
            { path: "terms", element: <Terms /> },
            { path: "privacy", element: <Privacy /> },
            { path: "dpa", element: <DPA /> },
            { path: "subprocessors", element: <Subprocessors /> },
            { path: "disclaimer", element: <Disclaimer /> },
            { path: "*", element: <PageNotFound /> },
          ],
        },
      ]),
    []
  );

  return (
    <div className="App">
      <RouterProvider router={router} />
    </div>
  );
}

export default App;
